import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Protect } from '@clerk/clerk-react';

import { Button, Card, Loader, Page, Stack } from '@nearst/ui';
import { useShop } from '@services/ShopProvider';
import { disableChannel } from '@services/channels';

import FeedQuality from '@components/Feed/FeedQuality';
import FeedDiagnostics from '@components/Feed/FeedDiagnostics';

import ChannelHeader from '../../components/ChannelHeader';
import FeedExport from './FeedExport.jsx';
import FeedUrl from './FeedUrl.jsx';
import useChannelInfo from '../useChannelInfo';
import styles from './ChannelIssues.module.scss';

const isViewable = (channelDefinition, status) => {
	if (!channelDefinition) return false;
	if (channelDefinition.comingSoon && status !== 'enabled') return false;
	return ['enabled', 'pending'].includes(status);
};

const FeedPending = ({ channelDefinition }) => (
	<Card title="Feed URL" description={`Your ${channelDefinition.title} feed is being set up.`}>
		<p>Our team is preparing your product feed. The feed URL will appear here once it is ready.</p>
	</Card>
);

const ChannelPending = ({ channelDefinition }) => (
	<Card title={channelDefinition.title} description={`${channelDefinition.title} is being set up.`}>
		<p>Our team has been notified of your interest. We'll be in touch to help get you set up.</p>
	</Card>
);

const DisableFeedChannel = ({ channelId, channelDefinition, shopId }) => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const handleDisable = async () => {
		setLoading(true);
		try {
			await disableChannel(channelId, shopId);
			navigate(`/channels/${shopId}`);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Card title="Disable channel" description={`Turn off your ${channelDefinition.title} product feed.`}>
			<Button onClick={handleDisable} disabled={loading}>
				Disable {channelDefinition.title} {loading && <Loader size={14} />}
			</Button>
		</Card>
	);
};

const ChannelIssues = () => {
	const { shop } = useShop();
	const { channelId, channelDefinition, shopChannelConfig, status } = useChannelInfo();

	if (!isViewable(channelDefinition, status)) {
		return <Navigate to={`/channels/${shop.id}`} />;
	}

	const feedUrl = shopChannelConfig?.config?.url;
	const isPending = status === 'pending';

	return (
		<Stack space="2rem">
			<ChannelHeader channelId={channelId} status={status} />
			<Page.Section className={styles.section}>
				{isPending && channelDefinition.feedExport && channelDefinition.manageUrl && (
					<div className={styles.banner}>
						<Link to={`/channels/${shop.id}/settings/${channelId}`}>
							Complete your setup
							<ChevronRightIcon height="1rem" />
						</Link>
					</div>
				)}

				{isPending && !channelDefinition.feedExport && !channelDefinition.comingSoon && (
					<ChannelPending channelDefinition={channelDefinition} />
				)}

				{channelDefinition.feedExport && feedUrl && <FeedUrl url={feedUrl} channelDefinition={channelDefinition} />}
				{channelDefinition.feedExport && !feedUrl && channelDefinition.comingSoon && (
					<FeedPending channelDefinition={channelDefinition} />
				)}

				<Protect permission="org:inventory:download">
					{!isPending && channelDefinition.feedExport && !channelDefinition.comingSoon && (
						<FeedExport channelDefinition={channelDefinition} shopId={shop.id} />
					)}
				</Protect>

				{!isPending && !channelDefinition.comingSoon && <FeedQuality channelId={channelDefinition.issuesChannelId} />}
				{!isPending && !channelDefinition.comingSoon && <FeedDiagnostics channelId={channelDefinition.issuesChannelId} />}

				{channelDefinition.comingSoon && status === 'enabled' && (
					<DisableFeedChannel channelId={channelId} channelDefinition={channelDefinition} shopId={shop.id} />
				)}
			</Page.Section>
		</Stack>
	);
};

export { ChannelIssues };
